export function isTeamLeader(user) {
  if (!user) return false;
  return Boolean(user.is_team_leader) || Number(user.management_id) === 1;
}

export function isHr(user) {
  if (!user) return false;
  return Boolean(user.is_hr) || Number(user.management_id) === 2;
}

/** Backend EventController@store currently allows any authenticated user. */
export function canCreateEvent(user) {
  return Boolean(user);
}

/** Mirrors Event::canBeModifiedBy() */
export function canModifyEvent(user, event) {
  if (!user || !event) return false;
  return isTeamLeader(user) || Number(event.created_by) === Number(user.id);
}

/** Event is linked to penalties, gallery items, or documents and cannot be deleted. */
export function eventHasDeleteBlockers(event) {
  if (!event) return false;
  const penalties = event.penalties_count ?? event.penaltiesCount ?? 0;
  const galleries = event.galleries_count ?? event.galleriesCount ?? 0;
  const documents = event.documents_count ?? event.documentsCount ?? 0;
  return Number(penalties) > 0 || Number(galleries) > 0 || Number(documents) > 0;
}

/** Human-readable reason why an event cannot be deleted. */
export function getEventDeleteBlockerMessage(event) {
  if (!event) return 'This event cannot be deleted because it is linked to other records.';

  const parts = [];
  const penalties = Number(event.penalties_count ?? event.penaltiesCount ?? 0);
  const galleries = Number(event.galleries_count ?? event.galleriesCount ?? 0);
  const documents = Number(event.documents_count ?? event.documentsCount ?? 0);

  if (penalties > 0) {
    parts.push(penalties === 1 ? '1 penalty record' : `${penalties} penalty records`);
  }
  if (galleries > 0) {
    parts.push(galleries === 1 ? '1 gallery item' : `${galleries} gallery items`);
  }
  if (documents > 0) {
    parts.push(documents === 1 ? '1 document' : `${documents} documents`);
  }

  if (parts.length === 0) {
    return 'This event cannot be deleted because it is linked to other records.';
  }

  const linked = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

  return `This event cannot be deleted because it is linked to ${linked}. Remove those links first.`;
}

/** User may cancel event (authorized; not already cancelled). */
export function canCancelEvent(user, event) {
  if (!canModifyEvent(user, event)) return false;
  return String(event?.status || '') !== 'Cancelled';
}

/** @deprecated Events cannot be deleted — use canCancelEvent */
export function canDeleteEvent() {
  return false;
}

/** Mirrors PenaltyController@store */
export function canCreatePenalty(user) {
  if (!user) return false;
  return isHr(user) || isTeamLeader(user);
}

/** True when the penalty assignee belongs to Human Resources (management_id 2). */
export function isPenaltyAssignedToHr(penalty) {
  const assignee = penalty?.assigned_to ?? penalty?.assignedTo;
  if (!assignee) return false;
  return Number(assignee.management_id) === 2 || Boolean(assignee.is_hr);
}

/** True when the penalty is assigned to the given user. */
export function isPenaltyAssignedToUser(penalty, user) {
  if (!penalty || !user) return false;
  return Number(penalty.assigned_to_id) === Number(user.id);
}

/** Mirrors Penalty::canBeModifiedBy() */
export function canModifyPenalty(user, penalty) {
  if (!user || !penalty) return false;
  if ((isTeamLeader(user) || isHr(user)) && isPenaltyAssignedToUser(penalty, user)) {
    return false;
  }
  if (isHr(user) && isPenaltyAssignedToHr(penalty)) {
    return false;
  }
  return (
    isHr(user) ||
    isTeamLeader(user) ||
    Number(penalty.assigned_by_id) === Number(user.id)
  );
}

/** Mirrors Penalty::canBeViewedBy() */
export function canViewPenalty(user, penalty) {
  if (!user || !penalty) return false;
  return (
    canModifyPenalty(user, penalty) ||
    Number(penalty.assigned_to_id) === Number(user.id)
  );
}
